import { EnrollmentMultiLayout } from '@egw/layouts'
import { Input, Button, Alert, AlertType, GenericError, ButtonVariant, Link } from '@egw/components'
import { Logger } from '@egw/logger'
import { CoreExternalRoutes, type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useState, useEffect, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useSubmitEnrollmentMultiSignInData } from '@egw/api'
import { useNavigate } from 'react-router'
import { Trans, useTranslation } from '@egw/i18n'
import { useFlags } from '@egw/feature-flags'
import { WealthDisclosures } from '@egw/features-system'
import { configClient } from '@egw/config'
import { PageHeader } from '../../../../components'
import { HtmlTestTypes, getTestId } from '../../../../utils/testing/get-test-id'
import { type Config } from '../../../../config/config'
import { ROUTES as MULTI_ROUTES } from '../../../../constants/routes/routes-multi'
import { enrollmentCookieService } from '@egw/enrollment-cookie'

const log = new Logger('Enrollment - Multi - SignIn')

interface FormData {
  email: string
  // AUTO GENERATED FORM DATA
}

export const SignInPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const { flags } = useFlags<FeatureFlags>()
  const config = configClient.getConfig() as Config

  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiSignInData()

  const [showError, setShowError] = useState(false)

  // Reset enrollment session when entering this page
  useEffect(() => {
    void enrollmentCookieService.resetSession()
  }, [])

  // AUTO GENERATED FORM HANDLERS START
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isValid, touchedFields },
  } = useForm({
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
  })

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      try {
        const response = await mutate({
          email: formData.email.toLowerCase(),
        })
        // Handle response
        if (
          !response ||
          (response.error &&
            [
              'REENTRY_INELIGIBLE',
              'INVALID_APPLICATION_STATE',
              'SESSION_NOT_FOUND',
              'INVALID_INPUT_COMBINATION',
            ].includes(response.error))
        ) {
          setShowError(true)
        } else if (response.nextStep === 'VERIFY_EMAIL') {
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${MULTI_ROUTES.RETURN_VERIFY_EMAIL.path}`)
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  return (
    <div className="bg-gray-50 h-full">
      <EnrollmentMultiLayout title={t('multi.return.sign_in.page_title')} className="flex !items-center">
        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        <>
          <div className="w-full flex flex-col justify-start p-6 bg-white mx-6 shadow-xl gap-5 rounded relative z-10 sm:max-w-[342px] md:w-[442px]">
            {/* AUTO GENERATED COMPONENTS START*/}
            <PageHeader title={t('multi.return.sign_in.title')} className="w-full text-center" />
            <form onSubmit={(e) => void onSubmit(e)}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: t('common.required_field'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]/i,
                    message: t('common.invalid_email'),
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('multi.return.sign_in.email_label')}
                    required
                    error={touchedFields.email ? formErrors.email?.message : undefined}
                    autoComplete="email"
                    type="email"
                    className="mb-xl"
                    testId={getTestId('email', HtmlTestTypes.input)}
                  />
                )}
              />

              <div className="flex-1 md:hidden" />

              {showError ? <Alert alertType={AlertType.Error} description={t('multi.return.sign_in.error')} className="mt-sm" /> : null}

              {/* AUTO GENERATED COMPONENTS */}

              <Button
                type="submit"
                className="w-full mt-xl"
                isDisabled={!isValid}
                isLoading={isSubmitting}
                variant={ButtonVariant.Secondary}
                testId="primaryAction_button"
              >
                {t('multi.return.sign_in.continue')}
              </Button>
            </form>
            {/* AUTO GENERATED COMPONENTS END*/}
          </div>
          <div className="pt-12 pb-8 mt-36 w-full">
            <div className="flex flex-col gap-2 text-xs text-meta text-left mx-auto w-full max-md:px-8">
              <WealthDisclosures />
            </div>
          </div>
        </>
      </EnrollmentMultiLayout>
    </div>
  )
}
